import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LoadingSpinner } from "@insite/mobius/index";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button, { ButtonPresentationProps } from "@waytek/MobiusOverrides/Button/Button";
import Pagination, { PaginationPresentationProps } from "@waytek/MobiusOverrides/Pagination/Pagination";
import PurchasedProductCard from "@waytek/Overrides/Components/PurchasedProductCard";
import loadPurchasedProducts from "@waytek/Store/Components/PurchasedProducts/Handlers/LoadPurchasedProducts";
import { CustomState } from "@waytek/Store/Reducers";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    title = "title",
    titlePosition = "titlePosition",
    purchaseType = "purchaseType",
    showBrand = "showBrand",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAvailability = "showAvailability",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",

    /* Verndale Custom Feature - New properties */
    isOrderHistory = "isOrderHistory",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.titlePosition]: string;
        [fields.purchaseType]: string;
        [fields.showBrand]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAvailability]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;

        /* Verndale Custom Feature - New properties */
        [fields.isOrderHistory]: boolean;
    };
}

const mapStateToProps = (state: CustomState, ownProps: OwnProps) => {
    const session = getSession(state);
    const isAuthenticated = (session.isAuthenticated || session.rememberMe) && !session.isGuest;

    return {
        products: getProductsForProductInfoList(state, ownProps.id),
        canViewPurchasedProducts: isAuthenticated,

        /* Verndale Custom Feature - START */
        language: state.context.session.language,
        currencySymbol: state.context.session.currency?.currencySymbol,
        pagination: state.purchasedProducts.byId[ownProps.id],
        loading: state.purchasedProducts.loading[ownProps.id] ?? false,
        parameter: state.pages.orderHistory.getOrdersParameter,
        /* Verndale Custom Feature - END */
    };
};

const mapDispatchToProps = {
    loadPurchasedProducts,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PurchasedProductsStyles {
    titleWrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    headerContainer?: GridContainerProps;
    itemHeaderGridItem?: GridItemProps;
    itemHeaderText?: TypographyPresentationProps;
    priceHeaderGridItem?: GridItemProps;
    priceHeaderText?: TypographyPresentationProps;
    addToCartHeaderGridItem?: GridItemProps;
    addToCartHeaderText?: TypographyPresentationProps;

    /* Verndale Custom Feature - START */
    wrapper?: InjectableCss;
    loadingSpinner?: LoadingSpinnerProps;
    moreDataContainer?: GridContainerProps;
    fullWidthGridItem?: GridItemProps;
    viewMoreLink?: ButtonPresentationProps;
    viewLessGridItem?: GridItemProps;
    viewLessLink?: ButtonPresentationProps;
    paginationGridItem?: GridItemProps;
    pagination?: PaginationPresentationProps;
    noProductsFoundText?: TypographyPresentationProps;
    /* Verndale Custom Feature - END */
}

export const purchasedProductsStyles: PurchasedProductsStyles = {
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 10px;
        `,
    },
    // eslint-disable-next-line
    /* eslint-disable sonarjs/no-nested-template-literals */
    headerContainer: {
        gap: 0,
        css: css`
            padding: 10px;
            background-color: ${getColor("common.accent")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: none;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    // eslint-disable-next-line
    /* eslint-enable sonarjs/no-nested-template-literals */
    itemHeaderGridItem: { width: [0, 0, 6, 6, 6] },
    itemHeaderText: { weight: "bold" },
    // eslint-disable-next-line
    /* eslint-disable sonarjs/no-nested-template-literals */
    priceHeaderGridItem: {
        width: [0, 0, 3, 3, 3],
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        padding-right: 35px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    // eslint-disable-next-line
    /* eslint-enable sonarjs/no-nested-template-literals */
    priceHeaderText: { weight: "bold" },
    addToCartHeaderGridItem: {
        width: [0, 0, 3, 3, 3],
        css: css`
            justify-content: flex-end;
        `,
    },
    addToCartHeaderText: { weight: "bold" },
    // VERNDALE CUSTOM FEATURE
    noProductsFoundText: {
        size: 24,
        weight: "bold",
        css: css`
            width: 100%;
            text-align: center;
        `,
    },
};

const styles = purchasedProductsStyles;

const getJustifyContent = (position: string) => {
    if (position === "left") {
        return "flex-start";
    }
    if (position === "right") {
        return "flex-end";
    }
    return "center";
};

const TitleWrapper = styled.div<{ position: string }>`
    display: flex;
    justify-content: ${({ position }) => getJustifyContent(position)};
`;

const PurchasedProducts = ({
    id,
    fields,
    products,
    canViewPurchasedProducts,
    loadPurchasedProducts,

    /* Verndale Custom Feature - START */
    language,
    currencySymbol,
    pagination,
    loading,
    parameter,
}: /* Verndale Custom Feature - END */
Props) => {
    /* Verndale Custom Feature */
    const [showMoreProducts, setShowMoreProducts] = React.useState(false);

    /* Verndale Custom Feature */
    const handleLoadPage = (page: number, pageSize = 5) => {
        loadPurchasedProducts({
            widgetId: id,
            purchaseType: fields.purchaseType,
            page,
            pageSize,
            isOrderHistory: fields.isOrderHistory,
            parameter,
        });
    };

    React.useEffect(() => {
        if (canViewPurchasedProducts) {
            /* Verndale Custom Feature */
            handleLoadPage(1, 5);
        }
    }, [fields.purchaseType, parameter]);

    /* Verndale Custom Feature */
    if (fields.isOrderHistory && products?.length === 0) {
        return (
            <Typography as="h2" {...styles.noProductsFoundText}>
                {translate("No products found")}
            </Typography>
        );
    }

    const showCondition = !canViewPurchasedProducts || !products || products.length === 0;
    if (!showMoreProducts && showCondition) {
        return null;
    }

    /* Verndale Custom Feature */
    const handleShowMoreLessResults = () => {
        handleLoadPage(1, !showMoreProducts ? 9 : 5);
        setShowMoreProducts(!showMoreProducts);
    };

    return (
        /* Verndale Custom Feature */
        <StyledWrapper {...styles.wrapper}>
            <TitleWrapper {...styles.titleWrapper} position={fields.titlePosition}>
                <Typography {...styles.titleText}>{fields.title}</Typography>
            </TitleWrapper>

            <GridContainer {...styles.headerContainer}>
                <GridItem {...styles.itemHeaderGridItem}>
                    <Typography {...styles.itemHeaderText}>{translate("Item")}</Typography>
                </GridItem>
                <GridItem {...styles.priceHeaderGridItem}>
                    <Typography {...styles.priceHeaderText}>{translate("Price")}</Typography>
                </GridItem>
                <GridItem {...styles.addToCartHeaderGridItem}>
                    <Typography {...styles.addToCartHeaderText}>{translate("Add to Cart")}</Typography>
                </GridItem>
            </GridContainer>

            <GridContainer {...styles.moreDataContainer}>
                {showMoreProducts && (
                    <>
                        <GridItem {...styles.viewLessGridItem}></GridItem>
                        <GridItem {...styles.paginationGridItem}>
                            {pagination && (
                                <Pagination
                                    {...styles.pagination}
                                    currentPage={pagination.currentPage}
                                    onChangeResultsPerPage={() => {}}
                                    onChangePage={handleLoadPage}
                                    resultsPerPage={pagination.defaultPageSize}
                                    resultsCount={pagination.totalItemCount}
                                    resultsPerPageOptions={pagination.pageSizeOptions}
                                    showResultsPerPage={false}
                                    scrollTop={false}
                                />
                            )}
                        </GridItem>
                    </>
                )}

                {loading && (
                    <GridItem {...styles.fullWidthGridItem}>
                        <LoadingSpinner {...styles.loadingSpinner} />
                    </GridItem>
                )}
            </GridContainer>

            {/* Verndale Custom Feature */}
            {products?.map(product => (
                <PurchasedProductCard
                    key={product.id}
                    product={product}
                    widgetId={id}
                    showBrand={fields.showBrand}
                    showPartNumbers={fields.showPartNumbers}
                    showPrice={fields.showPrice}
                    showAvailability={fields.showAvailability}
                    showAddToCart={fields.showAddToCart}
                    showAddToList={fields.showAddToList}
                    language={language}
                    currencySymbol={currencySymbol}
                />
            ))}

            <GridContainer {...styles.moreDataContainer}>
                {loading && (
                    <GridItem {...styles.fullWidthGridItem}>
                        <LoadingSpinner {...styles.loadingSpinner} />
                    </GridItem>
                )}

                {!showMoreProducts && (
                    <GridItem {...styles.fullWidthGridItem}>
                        <Button {...styles.viewMoreLink} onClick={handleShowMoreLessResults} className="button">
                            {translate("View more")}
                        </Button>
                    </GridItem>
                )}

                {showMoreProducts && (
                    <>
                        <GridItem {...styles.viewLessGridItem}>
                            <Button {...styles.viewLessLink} onClick={handleShowMoreLessResults} className="button">
                                {translate("View less")}
                            </Button>
                        </GridItem>
                        <GridItem {...styles.paginationGridItem}>
                            {pagination && (
                                <Pagination
                                    {...styles.pagination}
                                    currentPage={pagination.currentPage}
                                    onChangeResultsPerPage={() => {}}
                                    onChangePage={handleLoadPage}
                                    resultsPerPage={pagination.defaultPageSize}
                                    resultsCount={pagination.totalItemCount}
                                    resultsPerPageOptions={pagination.pageSizeOptions}
                                    showResultsPerPage={false}
                                    scrollTop={false}
                                />
                            )}
                        </GridItem>
                    </>
                )}
            </GridContainer>
            {/* Verndale Custom Feature - END */}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PurchasedProducts) as any,
    definition: {
        group: "Common",
        icon: "list-ul",
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.titlePosition,
                displayName: "Title Position",
                editorTemplate: "DropDownField",
                options: [
                    { value: "center", displayName: "Center" },
                    { value: "left", displayName: "Left" },
                    { value: "right", displayName: "Right" },
                ],
                /* Verndale Custom Feature */
                defaultValue: "left",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.purchaseType,
                displayName: "Purchase Type",
                editorTemplate: "DropDownField",
                options: [
                    { value: "recently", displayName: "Recently Purchased" },
                    { value: "frequently", displayName: "Frequently Purchased" },
                ],
                defaultValue: "recently",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showBrand,
                displayName: "Brand Name",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showAvailability,
                displayName: "Availability",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showAddToList,
                displayName: "Add to List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },

            /* Verndale Custom Feature - New properties */
            {
                name: fields.isOrderHistory,
                displayName: "Is Order history page?",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 9,
            },
        ],
    },
};

export default widgetModule;
